import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Box, Divider, ListItemIcon, Menu, MenuItem, Paper, Typography } from '@mui/material';
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import ThemeIcon from '@mui/icons-material/Palette';
import { useAuth } from '../lib/auth';
import { formatClock } from '../lib/time';
import { navBarGradient } from '../lib/theme';
import { useThemeId } from '../lib/theme-context';
import { ThemeDialog } from './ThemeDialog';

/**
 * Application shell: a sticky top bar over the page content.
 *
 * Navigation is the dashboard tile grid, not a sidebar — the legacy sidebar
 * re-queried permissions twice per menu item on every page render, and does not
 * suit field staff on phones.
 */

const APP_VERSION = `v${__APP_VERSION__}`;

export function AppLayout({ children }: { children: ReactNode }): React.JSX.Element {
    const navigate = useNavigate();
    const { me, signOut } = useAuth();
    const { themeId } = useThemeId();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [themeOpen, setThemeOpen] = useState(false);
    const [now, setNow] = useState(() => new Date());

    useEffect(() => {
        const t = setInterval(() => {
            setNow(new Date());
        }, 1000);
        return () => {
            clearInterval(t);
        };
    }, []);

    const initial = (me?.staffName ?? me?.email ?? '?').charAt(0).toUpperCase();
    const roleLabel = me?.isSuperAdmin === true ? 'Super Admin' : 'Staff';

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            <Paper
                square
                sx={{
                    px: { xs: 2, md: 4 },
                    // Installed on iOS the web view runs full-bleed under the
                    // status bar (apple-mobile-web-app-status-bar-style is
                    // black-translucent), so without this inset the bar sits
                    // beneath the clock and signal icons and the two overlap.
                    // In a browser tab the inset is 0 and this is just py: 1.5.
                    pt: 'calc(12px + env(safe-area-inset-top, 0px))',
                    pb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: navBarGradient(themeId),
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1100,
                }}
            >
                <Box
                    onClick={() => {
                        void navigate('/');
                    }}
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', minWidth: 0 }}
                >
                    {/* The same wordmark the login screen shows. Height is set,
                        not width, so it can never outgrow the bar on a narrow
                        phone; 350x82 makes that ~120px wide. Matched to the
                        32px avatar opposite so the two sides balance. */}
                    <Box
                        component="img"
                        src="/silverline-logo.png"
                        alt="Silverline Construction Group"
                        sx={{ height: { xs: 28, md: 32 }, width: 'auto', display: 'block' }}
                    />
                </Box>

                <Box
                    onClick={(e) => {
                        setMenuAnchor(e.currentTarget);
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 2,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                >
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: 14, fontWeight: 700 }}>
                        {initial}
                    </Avatar>
                    <Box sx={{ textAlign: 'left', display: { xs: 'none', md: 'block' } }}>
                        <Typography variant="body2" fontWeight={600} lineHeight={1.2} sx={{ color: 'common.white' }}>
                            {me?.staffName ?? me?.email ?? ''}
                        </Typography>
                        <Typography variant="caption" lineHeight={1.3} sx={{ color: 'rgba(255,255,255,0.75)' }}>
                            {roleLabel}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.1 }}>
                            <Typography
                                sx={{ fontSize: '0.65rem', opacity: 0.7, lineHeight: 1, color: 'common.white' }}
                            >
                                <span title={`Released ${__BUILD_TIME__}`}>{APP_VERSION}</span>
                            </Typography>
                            <Typography
                                sx={{ fontSize: '0.65rem', opacity: 0.5, lineHeight: 1, color: 'common.white' }}
                            >
                                ·
                            </Typography>
                            <Typography
                                sx={{ fontSize: '0.65rem', opacity: 0.7, lineHeight: 1, color: 'common.white' }}
                            >
                                {formatClock(now)}
                            </Typography>
                        </Box>
                    </Box>
                    <ArrowDownIcon
                        fontSize="small"
                        sx={{ color: 'rgba(255,255,255,0.75)', ml: 0.5, display: { xs: 'none', md: 'block' } }}
                    />
                </Box>

                <Menu
                    anchorEl={menuAnchor}
                    open={menuAnchor !== null}
                    onClose={() => {
                        setMenuAnchor(null);
                    }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{ paper: { elevation: 4, sx: { mt: 1, minWidth: 210, borderRadius: 2 } } }}
                >
                    <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                            {me?.email ?? ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {roleLabel}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>
                                <span title={`Released ${__BUILD_TIME__}`}>{APP_VERSION}</span>
                            </Typography>
                            <Typography
                                sx={{ fontSize: '0.65rem', color: 'text.disabled', opacity: 0.6, lineHeight: 1 }}
                            >
                                ·
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>
                                {formatClock(now)}
                            </Typography>
                        </Box>
                    </Box>
                    <Divider />
                    <MenuItem
                        onClick={() => {
                            setMenuAnchor(null);
                            setThemeOpen(true);
                        }}
                    >
                        <ListItemIcon>
                            <ThemeIcon fontSize="small" />
                        </ListItemIcon>
                        Theme
                    </MenuItem>
                    <Divider />
                    <MenuItem
                        onClick={() => {
                            setMenuAnchor(null);
                            void signOut();
                        }}
                        sx={{ color: 'error.main' }}
                    >
                        <ListItemIcon>
                            <LogoutIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        Sign out
                    </MenuItem>
                </Menu>
            </Paper>

            <ThemeDialog
                open={themeOpen}
                onClose={() => {
                    setThemeOpen(false);
                }}
            />

            {children}
        </Box>
    );
}
